import type { Node as ProseMirrorNode } from 'prosemirror-model'

export const FLASH_BLOCK_SEPARATOR = '\n'

const COMPLETED_WORD = /^(\S+\s)/u
const FNV_OFFSET = 2166136261
const FNV_PRIME = 16777619

export interface FlashLockFingerprint {
  length: number
  hash: number
}

export function hashPlainText(text: string, seed = FNV_OFFSET): number {
  let hash = seed >>> 0
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, FNV_PRIME)
  }
  return hash >>> 0
}

export function plainTextOfDoc(doc: ProseMirrorNode): string {
  return doc.textBetween(0, doc.content.size, FLASH_BLOCK_SEPARATOR, FLASH_BLOCK_SEPARATOR)
}

export function advanceFlashLock(locked: string, full: string): string {
  if (!full.startsWith(locked)) return locked
  let next = locked
  let tail = full.slice(locked.length)
  while (tail.length > 0) {
    const match = COMPLETED_WORD.exec(tail)
    if (!match) break
    next += match[0]
    tail = tail.slice(match[0].length)
  }
  return next
}

export function positionForPlainTextLength(
  doc: ProseMirrorNode,
  targetLength: number,
  from = 0,
): number | null {
  if (targetLength <= 0) return from
  let textLength = 0
  let firstBlock = true
  let found: number | null = null

  doc.nodesBetween(from, doc.content.size, (node, pos) => {
    if (found != null) return false
    const leafText = node.isLeaf ? FLASH_BLOCK_SEPARATOR : ''
    if (node.isBlock && ((node.isLeaf && leafText) || node.isTextblock)) {
      if (firstBlock) {
        firstBlock = false
      } else {
        textLength += FLASH_BLOCK_SEPARATOR.length
        if (textLength === targetLength) {
          found = pos + 1
          return false
        }
      }
    }
    if (node.isText) {
      const text = node.text ?? ''
      const startOffset = Math.max(0, from - pos)
      const slice = text.slice(startOffset)
      for (let i = 0; i < slice.length; i += 1) {
        textLength += 1
        if (textLength === targetLength) {
          found = pos + startOffset + i + 1
          return false
        }
      }
      return
    }
    if (!node.isLeaf || !leafText) return
    for (let i = 0; i < leafText.length; i += 1) {
      textLength += 1
      if (textLength === targetLength) {
        found = pos + node.nodeSize
        return false
      }
    }
  })

  if (found == null) return null
  const actual = doc.textBetween(from, found, FLASH_BLOCK_SEPARATOR, FLASH_BLOCK_SEPARATOR)
  if (actual.length !== targetLength) return null
  return found
}
